import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  TableCell,
  TableRow,
  Typography,
  Tooltip,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  TD_SX,
  WORK_OPTIONS,
  WORK_METHOD_OPTIONS,
  FINANCE_OPTIONS,
  BG_USAGE_OPTIONS,
  REPORT_OPTIONS,
} from '../constants';
import {
  getPoNetOfVat,
  getBgColumnValues,
  getEstimatedProfit,
  getProjectedFund,
  getPaidValue,
  getExpectedValue,
  getRefundPendingValue,
  getTargetProfit,
} from '../formulas';
import { fmt, fmtDate } from '../utils';
import { useL } from '../LangContext';
import {
  NumCell,
  PctCell,
  InlineNumberCell,
  InlineNoteCell,
  ReviewCheckCell,
  InlineAmountCell,
} from './cells';

// สีตัวย่อ W/D/L — ให้ตรงกับสีของ LEGAL_OPTIONS ใน P111 (ProjectSingleDashboard.js) เป๊ะ
const LEGAL_FLAG_COLORS = { W: '#7c3aed', D: '#dc2626', L: '#991b1b' };

// "W" ต่อท้ายด้วยจำนวนวันที่เหลือ/เกินกำหนด warranty เป็น "W124" เหมือน P111 — D/L ไม่เปลี่ยน
const legalFlagLabel = (f, warrantyDaysLeft) =>
  (f === 'W' && warrantyDaysLeft != null ? `W${Math.abs(warrantyDaysLeft)}` : f);

const ProjectDashboardRow = React.memo(function ProjectDashboardRow({
  row, idx, mergedRow,
  showCol2, showGroup1,
  projectFilter, customerFilter,
  stickyLeftOf, getW, getVal, getCustomerName, sameCustomerAsPrev,
  onClickProject, scheduleClick, cancelAndRun,
  setProjectFilter, setCustomerFilter, onToggleExcludeProject, onToggleExcludeCustomer,
  monthFilter, yearFilter, setMonthFilter, setYearFilter,
  onToggleExcludeMonth, onToggleExcludeYear,
  onToggleZeroExclude,
  renderSeedCell, todayReviewStr,
  isPinned,
  onOpenActionRequired,
}) {
  const theme = useTheme();
  const L = useL();
  const po = getPoNetOfVat(row);
  const received = row.received ?? 0; // col 7
  const billedWaiting = row.billed_waiting ?? 0; // col 8
  const progress = getVal(row, 'progress') ?? 0;
  const note = getVal(row, 'dashboard_note') ?? '';
  // แสดงในเซลล์แค่บันทึกล่าสุด (บรรทัดแรก) — ของเดิมเป็น log สะสมหลายรายการ
  // ถ้าโชว์เต็มจะดันแถวสูงขึ้นเรื่อยๆ ตามจำนวนบันทึก ดู tooltip ที่ hover แถวเพื่อดูทั้งหมด
  const latestNote = note.split('\n')[0] || '';
  const bgUsedPct = row.bg_used_pct ?? 0;
  const isOdd = idx % 2 === 0;
  const isLawsuit = !!getVal(row, 'legal_lawsuit');
  const rowBg = isLawsuit ? '#fff1f1' : isOdd ? '#ffffff' : '#f6f8fc';
  // สีทึบ (ไม่ใช่ rgba โปร่งแสง) เท่ากับ primary.main (#4f46e5) เกลี่ย 16% บนพื้นขาว — ใช้ค่าทึบ
  // เพราะเซลล์ล็อก (sticky) ต้อง bgcolor ทึบเสมออยู่แล้ว (กันเนื้อหาที่เลื่อนผ่านทะลุออกมาเห็น) ถ้าใช้
  // alpha()/rgba() ตรงนี้ พอ <TableRow>&:hover ก็เป็น rgba ชุดเดียวกันอยู่แล้ว เซลล์ล็อกที่วางทับซ้อน
  // บน TableRow อีกทีจะกลายเป็นซ้อน rgba 2 ชั้น (เข้มกว่าคอลัมน์ปกติที่โดนแค่ชั้นเดียว) สีเลยไม่ตรงกัน
  // ทั้งแถว — ใช้ค่าทึบตัวเดียวกันทั้ง TableRow และเซลล์ล็อกแทน ปัญหาซ้อนสีเลยหมดไป
  const hoverBg = isLawsuit ? '#ffe5e5' : '#e3e1fb';
  const [isHovered, setIsHovered] = useState(false);
  const frzTd = (col) =>
    stickyLeftOf[col] !== undefined
      ? {
          position: 'sticky',
          left: stickyLeftOf[col],
          zIndex: 3,
          bgcolor: isHovered ? hoverBg : rowBg,
        }
      : {};
  const { col14, col15, col16, col17, col12, bgUsageDisabled } = getBgColumnValues(mergedRow);
  // คลิกขวาที่เซลล์คอลัมน์ตัวเลข/วันที่ — exclude ทั้งคอลัมน์ถ้าค่าที่คลิกเป็น 0/ว่าง (ดู
  // toggleZeroExclude ใน ProjectDashboardTable.js — ไม่มีผลถ้าค่าที่คลิกไม่ใช่ 0/ว่าง)
  const zeroExcludeHandler = (key, value) => (e) => {
    e.preventDefault();
    onToggleZeroExclude(key, value);
  };
  const realprofit = getEstimatedProfit(mergedRow); // เหมือน estimatedProfitForPercent ใน ProjectPopup.js
  const actionRequired = getVal(row, 'action_required');
  const actionDays = actionRequired?.createdAt
    ? Math.max(0, dayjs().startOf('day').diff(dayjs(actionRequired.createdAt).startOf('day'), 'day'))
    : 0;
  const realPct = po > 0 ? (realprofit / po) * 100 : 0;
  const projectedFund = getProjectedFund(mergedRow); // col10 = col33(BG) - col11(ใช้ไปแล้ว)
  const targetProfit = getTargetProfit(mergedRow); // col18 = PO(cost) - BG ที่ตั้งไว้ (เหมือน P111 num 5)
  const projectName = row.name || '';
  const customerName = getCustomerName(row) || '';
  const rowMonth = row.oldest_job_date ? new Date(row.oldest_job_date).getMonth() + 1 : null;
  const rowYear = row.oldest_job_date ? new Date(row.oldest_job_date).getFullYear() : null;
  const projectNameRef = useRef(null);
  const customerNameRef = useRef(null);
  const [isProjectNameOverflow, setIsProjectNameOverflow] = useState(false);
  const [isCustomerNameOverflow, setIsCustomerNameOverflow] = useState(false);

  useEffect(() => {
    const projectEl = projectNameRef.current;
    const customerEl = customerNameRef.current;
    setIsProjectNameOverflow(
      !!projectEl && projectEl.scrollWidth > projectEl.clientWidth,
    );
    setIsCustomerNameOverflow(
      !!customerEl && customerEl.scrollWidth > customerEl.clientWidth,
    );
  }, [projectName, customerName]);

  return (
    <TableRow
        sx={{
          position: actionRequired?.text ? 'relative' : undefined,
          zIndex: actionRequired?.text ? 8 : undefined,
          bgcolor: isPinned ? alpha(theme.palette.primary.main, 0.1) : rowBg,
          transition: 'background-color 0.1s ease',
          ...(isPinned && {
            boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
          }),
          '&:hover': {
            bgcolor: hoverBg,
          },
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onDoubleClick={(e) => {
          if (e.target.closest('[data-action-required-trigger="true"]')) {
            e.preventDefault();
            e.stopPropagation();
            onOpenActionRequired?.(row);
            return;
          }
          cancelAndRun(() => onClickProject?.(row._id));
        }}
      >
        {/* # */}
        <TableCell
          data-action-required-trigger="true"
          onDoubleClickCapture={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpenActionRequired?.(row);
          }}
          sx={{
            ...TD_SX,
            width: 38,
            minWidth: 38,
            maxWidth: 38,
            textAlign: 'center',
            color: '#94a3b8',
            fontSize: '0.65rem',
            userSelect: 'none',
            cursor: 'pointer',
            overflow: 'visible',
            ...frzTd(0),
            ...(actionRequired?.text && { zIndex: 20 }),
          }}
        >
          {idx + 1}
          {actionRequired?.text && (
            <Box
              sx={{
                  position: 'absolute',
                  left: '50vw',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 30,
                  maxWidth: '55vw',
                  px: 1.2,
                  py: 0.45,
                  borderRadius: 1,
                  bgcolor: '#fecaca',
                  color: '#991b1b',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
                  fontSize: '0.5rem',
                  fontWeight: 700,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  cursor: 'default',
                  transition: 'opacity 120ms ease',
                  '&:hover': { opacity: 0 },
              }}
            >
              Action required: {actionRequired.text} · {actionDays} วัน
              {' · '}บันทึกโดย {actionRequired.createdBy || 'KPP'}
              {' · '}{dayjs(actionRequired.createdAt).format('DD/MM/YYYY')}
            </Box>
          )}
        </TableCell>

        {/* JOB */}
        <TableCell
          sx={{
            ...TD_SX,
            width: getW(1),
            minWidth: getW(1),
            maxWidth: getW(1),
            ...frzTd(1),
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color:
                projectFilter === row.project_number
                  ? '#7c3aed'
                  : 'primary.main',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={() =>
              scheduleClick(() =>
                setProjectFilter(
                  projectFilter === row.project_number
                    ? ''
                    : row.project_number,
                ),
              )
            }
          >
            {row.project_number || '-'}
          </Typography>
        </TableCell>

                    {/* STATUS — 3 dimensions */}
                    {showCol2 && (() => {
                      const ws = getVal(row, 'work_status') || '';
                      const fs = getVal(row, 'finance_status') || '';
                      const lw = getVal(row, 'legal_warranty');
                      const ld = getVal(row, 'legal_debt');
                      const ll = getVal(row, 'legal_lawsuit');
                      const warrantyEndDate = getVal(row, 'warranty_end_date');
                      const warrantyDaysLeft = warrantyEndDate
                        ? dayjs(warrantyEndDate).startOf('day').diff(dayjs().startOf('day'), 'day')
                        : null;
                      const bgUsageCategory =
                        getVal(row, 'bg_usage_category') || '';
                      const reportStatus = getVal(row, 'report_status') || '';
                      const workMethod = getVal(row, 'work_method') || '';
                      const workMethodOpt = WORK_METHOD_OPTIONS.find(
                        (o) => o.key === workMethod,
                      );
                      const workOpt = WORK_OPTIONS.find((o) => o.key === ws);
                      const finOpt = FINANCE_OPTIONS.find((o) => o.key === fs);
                      const bgUsageOpt = BG_USAGE_OPTIONS.find(
                        (o) => o.key === bgUsageCategory,
                      );
                      const reportOpt = REPORT_OPTIONS.find(
                        (o) => o.key === reportStatus,
                      );
                      const legalFlags = [
                        lw ? 'W' : null,
                        ld ? 'D' : null,
                        ll ? 'L' : null,
                      ].filter(Boolean);
                      const financeColor =
                        fs === 'last_installment'
                          ? '#111827'
                          : finOpt?.color || '#475569';
                      const reportTag = !reportStatus
                        ? L('รายงาน?', 'Report?')
                        : reportStatus === 'pending'
                        ? L('รายงานค้าง', 'Report due')
                        : reportStatus === 'submitted'
                        ? L('รายงานส่งแล้ว', 'Report sent')
                        : L('ไม่มีรายงาน', 'No report');
                      const workShort =
                        ws === 'done'
                          ? L('เสร็จ 100%', 'Done 100%')
                          : ws === 'in_progress'
                          ? `${L('ดำเนินการ', 'Working')}${workMethodOpt ? ` (${L(
                              workMethodOpt.shortLabel,
                              workMethod === 'self'
                                ? 'Self'
                                : workMethod === 'hired'
                                ? 'Outsource'
                                : 'Mixed',
                            )})` : ''}`
                          : ws === 'waiting'
                          ? L('รอเริ่ม', 'Waiting')
                          : L('งาน:ไม่ระบุ', 'Work: N/A');
                      const financeShort =
                        fs === 'completed_po'
                          ? L('รับครบแล้ว', 'Paid 100%')
                          : fs === 'last_installment'
                          ? L('รอรับงวดสุดท้าย', 'Final due')
                          : fs === 'billed_waiting'
                          ? L('วางบิลรอรับ', 'Billed')
                          : fs === 'partial'
                          ? L('เบิกบางส่วน', 'Partial')
                          : fs === 'none'
                          ? L('ยังไม่เคยเบิก', 'Not billed')
                          : L('เงิน:ไม่ระบุ', 'Finance: N/A');
                      // งวดสุดท้าย/วางบิลรอรับ — โผล่ต่อท้ายบรรทัดสถานะงานเลย ไม่ต้องขึ้นบรรทัดใหม่
                      const appendFinanceInline =
                        fs === 'last_installment' || fs === 'billed_waiting';
                      const financeInlineText =
                        fs === 'last_installment'
                          ? L('รอรับเงิน', 'Await pay')
                          : financeShort;
                      const reportColor = reportStatus ? (reportOpt?.color || '#334155') : '#dc2626';
                      const segments = [
                        {
                          text: workOpt?.label || '— งาน —',
                          color: workOpt?.color || '#64748b',
                          weight: 600,
                        },
                        {
                          text: finOpt?.label || '— การเงิน —',
                          color: financeColor,
                          weight: 600,
                        },
                        bgUsageOpt
                          ? {
                              text: bgUsageOpt.label,
                              color: bgUsageOpt.color,
                              weight: 500,
                            }
                          : null,
                        {
                          text: reportStatus
                            ? `รายงาน:${reportOpt?.label || '-'}`
                            : 'รายงาน:ยังไม่ระบุ',
                          color: reportColor,
                          weight: reportStatus ? 500 : 700,
                        },
                        legalFlags.length > 0
                          ? {
                              text: `นิติ:${legalFlags.map((f) => legalFlagLabel(f, warrantyDaysLeft)).join('/')}`,
                              color: '#7c3aed',
                              weight: 600,
                            }
                          : null,
                      ].filter(Boolean);

                      const tooltipText = [
                        `${L('งาน', 'Work')}: ${workShort}`,
                        `${L('การเงิน', 'Finance')}: ${financeShort}`,
                        bgUsageOpt ? `${L('งบ', 'BG')}: ${L(bgUsageOpt.label, 'BG closed')}` : null,
                        `${L('รายงาน', 'Report')}: ${reportTag}`,
                        lw ? 'Warrant/Guarantee' : null,
                        ld ? L('เจ้าหนี้ค้างจ่าย', 'Overdue Payable') : null,
                        ll ? L('ฟ้องร้อง', 'Lawsuit') : null,
                      ]
                        .filter(Boolean)
                        .join('\n');
                      return (
                        <TableCell
                          sx={{
                            ...TD_SX,
                            position: 'relative',
                            ...frzTd(2),
                            width: getW(2),
                            minWidth: getW(2),
                            maxWidth: getW(2),
                            userSelect: 'none',
                            py: 0.15,
                          }}
                        >
                          {bgUsageOpt && (
                            <Box
                              component="span"
                              sx={{
                                position: 'absolute',
                                right: 3,
                                bottom: 1,
                                fontSize: '0.64rem',
                                color: bgUsageOpt.color,
                                lineHeight: 1,
                                pointerEvents: 'none',
                              }}
                            >
                              -BG
                            </Box>
                          )}
                          <Tooltip
                            title={tooltipText}
                            placement="top"
                            arrow
                            slotProps={{
                              tooltip: {
                                sx: {
                                  whiteSpace: 'pre-wrap',
                                  maxWidth: 300,
                                },
                              },
                            }}
                          >
                            <Stack spacing={0} sx={{ minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                  color: workOpt?.color || '#64748b',
                                  fontWeight: 400,
                                  fontSize: '0.66rem',
                                  lineHeight: 1.05,
                                }}
                              >
                                {workShort}
                                {appendFinanceInline && (
                                  <Box
                                    component="span"
                                    sx={{
                                      ml: 0.45,
                                      color: financeColor,
                                      fontWeight: 400,
                                    }}
                                  >
                                    {financeInlineText}
                                  </Box>
                                )}
                              </Typography>
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                  fontSize: '0.64rem',
                                  lineHeight: 1.05,
                                }}
                              >
                                {!appendFinanceInline && (
                                  <>
                                    <Box component="span" sx={{ color: financeColor, fontWeight: 400 }}>
                                      {financeShort}
                                    </Box>
                                    <Box component="span" sx={{ color: '#94a3b8', mx: 0.2 }}>
                                      |
                                    </Box>
                                  </>
                                )}
                                <Box component="span" sx={{ color: reportColor, fontWeight: 400 }}>
                                  {reportTag}
                                </Box>
                                {legalFlags.length > 0 && (
                                  <Box component="span" sx={{ ml: 0.35, fontWeight: 400 }}>
                                    (
                                    {legalFlags.map((f, i) => (
                                      <React.Fragment key={f}>
                                        {i > 0 && (
                                          <Box component="span" sx={{ color: '#94a3b8' }}>
                                            /
                                          </Box>
                                        )}
                                        <Box component="span" sx={{ color: LEGAL_FLAG_COLORS[f] }}>
                                          {legalFlagLabel(f, warrantyDaysLeft)}
                                        </Box>
                                      </React.Fragment>
                                    ))}
                                    )
                                  </Box>
                                )}
                              </Typography>
                            </Stack>
                          </Tooltip>
                        </TableCell>
                      );
                    })()}

                    {/* ตรวจสอบรายวัน */}
                    <TableCell
                      sx={{
                        ...TD_SX,
                        width: getW(36),
                        minWidth: getW(36),
                        textAlign: 'left',
                        ...frzTd(36),
                      }}
                    >
                      <ReviewCheckCell
                        reviewChecks={getVal(row, 'review_checks')}
                        todayStr={todayReviewStr}
                      />
                    </TableCell>

                    {/* % ความก้าวหน้า */}
                    <TableCell
                      sx={{
                        ...TD_SX,
                        width: getW(3),
                        minWidth: getW(3),
                        maxWidth: getW(3),
                        textAlign: 'center',
                        ...frzTd(3),
                      }}
                      onContextMenu={zeroExcludeHandler('progress', progress)}
                    >
                      <InlineNumberCell value={progress} />
                    </TableCell>

                    {/* ผู้ว่าจ้าง */}
                    <TableCell
                      sx={{
                        ...TD_SX,
                        width: getW(5),
                        minWidth: getW(5),
                        maxWidth: getW(5),
                        ...frzTd(5),
                      }}
                    >
                      <Tooltip
                        title={customerName}
                        placement="top"
                        arrow
                        disableInteractive
                        disableHoverListener={!isCustomerNameOverflow}
                      >
                        <Typography
                          ref={customerNameRef}
                          variant="caption"
                          sx={{
                            display: 'block',
                            pl: sameCustomerAsPrev ? 0 : 0.5,
                            textAlign: sameCustomerAsPrev ? 'center' : 'left',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                            color: 'text.primary',
                            '&:hover': {
                              color: 'primary.main',
                              textDecoration: 'underline',
                            },
                          }}
                          onClick={() =>
                            scheduleClick(() =>
                              setCustomerFilter(
                                customerFilter === customerName
                                  ? ''
                                  : customerName,
                              ),
                            )
                          }
                          onContextMenu={(e) => {
                            e.preventDefault();
                            onToggleExcludeCustomer(customerName);
                          }}
                        >
                          {sameCustomerAsPrev ? "---'---" : customerName || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* โครงการ */}
                    <TableCell
                      sx={{
                        ...TD_SX,
                        width: getW(4),
                        minWidth: getW(4),
                        maxWidth: getW(4),
                        position: 'relative',
                        ...frzTd(4),
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.3}
                        alignItems="flex-start"
                        sx={{ pl: 0.5 }}
                      >
                        <Tooltip
                          title={projectName}
                          placement="top"
                          arrow
                          disableInteractive
                          disableHoverListener={!isProjectNameOverflow}
                        >
                          <Typography
                            ref={projectNameRef}
                            variant="caption"
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              display: 'block',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontSize: '0.7rem',
                              cursor: 'pointer',
                              color:
                                projectFilter === row.project_number
                                  ? '#1e40af'
                                  : '#1d4ed8',
                              '&:hover': {
                                color: '#1e40af',
                                textDecoration: 'underline',
                              },
                            }}
                            onClick={() =>
                              scheduleClick(() =>
                                setProjectFilter(
                                  projectFilter === row.project_number
                                    ? ''
                                    : row.project_number,
                                ),
                              )
                            }
                            onContextMenu={(e) => {
                              e.preventDefault();
                              onToggleExcludeProject(row.project_number);
                            }}
                          >
                            {projectName || '-'}
                          </Typography>
                        </Tooltip>
                        {/* col14 ยังคำนวณติดลบได้ปกติแม้โปรเจกต์ตั้ง "ไม่ใช้ BG แล้ว" (bg_usage_category
                            = no_bg_usage) เพราะ getBgColumnValues ไม่ได้เช็คเงื่อนไขนี้ในตัวสูตรเอง —
                            เลยต้องกันธง "เกิน BG" ไม่ให้ขึ้นตรงนี้แทน (ไม่งั้นเตือนเกินงบทั้งที่ผู้ใช้
                            บอกไปแล้วว่าโปรเจกต์นี้ไม่ยึด BG เป็นเกณฑ์) */}
                        {col14 !== null && !bgUsageDisabled && (
                          <Tooltip
                            title={
                              projectedFund < 0
                                ? 'งบโครงการเกิน'
                                : 'BG ตอนนี้ยังพอ แต่รวมภาระอนาคตที่ต้องจ่ายอีกแล้วเกิน'
                            }
                            placement="top"
                            slotProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: projectedFund < 0 ? '#dc2626' : '#f97316',
                                },
                              },
                            }}
                          >
                            <FlagIcon
                              sx={{
                                fontSize: 13,
                                color: projectedFund < 0 ? '#dc2626' : '#f97316',
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                        )}
                        {!row.total_budget && (
                          <Tooltip
                            title="no Budget Control"
                            placement="top"
                            slotProps={{
                              tooltip: {
                                sx: { bgcolor: '#dc2626' },
                              },
                            }}
                          >
                            <ErrorOutlineIcon
                              sx={{
                                fontSize: 13,
                                color: '#dc2626',
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                      {/* ธง (col14 !== null, บรรทัด ~511) แสดงว่าเกิน BG รวมอยู่แล้ว — ไม่ต้องซ้อน
                          สามเหลี่ยม "เกิน BG ย่อย" ทับอีก ซ้ำซ้อนเพราะสื่อความหมายเดียวกัน (เกินงบ)
                          โชว์สามเหลี่ยมเฉพาะตอนที่ธงไม่ขึ้นเท่านั้น — และไม่โชว์เลยถ้าโปรเจกต์ตั้ง
                          "ไม่ใช้ BG แล้ว" เหมือนธงด้านบน */}
                      {row.has_over_budget_category && col14 === null && !bgUsageDisabled && (
                        <Tooltip
                          title={`เกิน BG: ${(
                            row.over_budget_category_names || []
                          ).join(', ')}`}
                          placement="top-start"
                          slotProps={{
                            tooltip: {
                              sx: { bgcolor: '#c2185b' },
                            },
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              right: 3,
                              bottom: 2,
                              fontSize: '0.9rem',
                              color: '#e91e8c',
                              lineHeight: 1,
                              cursor: 'default',
                            }}
                          >
                            ⚠
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>

                    {showGroup1 && (
                      <>
                        {/* Month */}
                        <TableCell
                          sx={{ ...TD_SX, width: getW(37), minWidth: getW(37), maxWidth: getW(37), textAlign: 'center' }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            if (rowMonth !== null) onToggleExcludeMonth(rowMonth);
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.65rem',
                              cursor: rowMonth !== null ? 'pointer' : 'default',
                              color: monthFilter === rowMonth ? '#1d4ed8' : 'text.primary',
                              '&:hover':
                                rowMonth !== null
                                  ? { color: '#1d4ed8', textDecoration: 'underline' }
                                  : undefined,
                            }}
                            onClick={() => {
                              if (rowMonth === null) return;
                              scheduleClick(() =>
                                setMonthFilter(monthFilter === rowMonth ? '' : rowMonth),
                              );
                            }}
                          >
                            {row.oldest_job_date
                              ? String(rowMonth).padStart(2, '0')
                              : '-'}
                          </Typography>
                        </TableCell>

                        {/* Year */}
                        <TableCell
                          sx={{ ...TD_SX, width: getW(38), minWidth: getW(38), maxWidth: getW(38), textAlign: 'center' }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            if (rowYear !== null) onToggleExcludeYear(rowYear);
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.65rem',
                              cursor: rowYear !== null ? 'pointer' : 'default',
                              color: yearFilter === rowYear ? '#1d4ed8' : 'text.primary',
                              '&:hover':
                                rowYear !== null
                                  ? { color: '#1d4ed8', textDecoration: 'underline' }
                                  : undefined,
                            }}
                            onClick={() => {
                              if (rowYear === null) return;
                              scheduleClick(() =>
                                setYearFilter(yearFilter === rowYear ? '' : rowYear),
                              );
                            }}
                          >
                            {row.oldest_job_date
                              ? String(rowYear % 100).padStart(2, '0')
                              : '-'}
                          </Typography>
                        </TableCell>
                      </>
                    )}

                    {/* PO */}
                    <TableCell
                      sx={{
                        ...TD_SX,
                        textAlign: 'right',
                        width: getW(6),
                        minWidth: getW(6),
                        maxWidth: getW(6),
                        ...frzTd(6),
                      }}
                      onContextMenu={zeroExcludeHandler('po', po)}
                    >
                      <NumCell value={po} />
                    </TableCell>

                    {showGroup1 &&
                      (() => {
                        const seedReceived = getVal(row, 'seed_received') ?? 0;
                        const seedBilled =
                          getVal(row, 'seed_billed_waiting') ?? 0;
                        return (
                          <>
                            {/* col 7: วางบิลแล้วรอรับเงิน + ค่าตั้งต้น — ไม่ต้องขึ้นแดงแม้ติดลบ */}
                            {renderSeedCell({
                              field: 'seed_billed_waiting',
                              seedValue: seedBilled,
                              autoValue: billedWaiting,
                              redOnNegative: false,
                              width: getW(7),
                              excludeKey: 'billed_waiting',
                            })}
                            {/* col 8: รับเงินมาแล้ว + ค่าตั้งต้น */}
                            {renderSeedCell({
                              field: 'seed_received',
                              seedValue: seedReceived,
                              autoValue: received,
                              width: getW(8),
                              excludeKey: 'received',
                            })}
                          </>
                        );
                      })()}

                    {/* col 9: ยอดค้างส่งงานจาก PO — แสดงเสมอ ไม่ผูกกับ toggle กลุ่มไหนแล้ว */}
                    {(() => {
                      const seedReceived = getVal(row, 'seed_received') ?? 0;
                      const seedBilled = getVal(row, 'seed_billed_waiting') ?? 0;
                      const effPoRemaining =
                        po - (received + seedReceived) - (billedWaiting + seedBilled);
                      return (
                        <TableCell
                          sx={{ ...TD_SX, textAlign: 'right', width: getW(9) }}
                          onContextMenu={zeroExcludeHandler('po_remaining', effPoRemaining)}
                        >
                          <NumCell value={effPoRemaining} redWhenNegative />
                        </TableCell>
                      );
                    })()}

                    {showGroup1 && (
                      <>
                        {/* col18: Profit Target = PO(cost) - BG ที่ตั้งไว้ (เหมือน P111 num 5) */}
                        <TableCell
                          sx={{ ...TD_SX, textAlign: 'right', width: getW(18) }}
                          onContextMenu={zeroExcludeHandler('target_profit', targetProfit)}
                        >
                          <NumCell value={targetProfit} />
                        </TableCell>

                        {/* col33: BG (วงเงิน = total_budget) */}
                        <TableCell
                          sx={{ ...TD_SX, textAlign: 'right', width: getW(33) }}
                          onContextMenu={zeroExcludeHandler('total_budget', row.total_budget)}
                        >
                          <NumCell value={row.total_budget} />
                        </TableCell>
                      </>
                    )}

                    {/* ใช้ไปแล้ว (Paid) */}
                    <TableCell
                      sx={{ ...TD_SX, textAlign: 'right', width: getW(11) }}
                      onContextMenu={zeroExcludeHandler('total_expenses', getPaidValue(row))}
                    >
                      <NumCell value={getPaidValue(row)} />
                    </TableCell>

                    {/* ทุนคาดการณ์ = BG(col33) - ใช้ไปแล้ว(col11) */}
                    <TableCell
                      sx={{ ...TD_SX, textAlign: 'right', width: getW(10), position: 'relative' }}
                      onContextMenu={zeroExcludeHandler('projected_fund', projectedFund)}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          whiteSpace: 'nowrap',
                          textAlign: 'right',
                          fontSize: '0.65rem',
                          color: projectedFund > 0 ? 'text.primary' : 'text.disabled',
                        }}
                      >
                        {fmt(projectedFund)}
                      </Typography>
                      {po > 0 && projectedFund > 0 && (() => {
                        const pct = (projectedFund / po) * 100;
                        return (
                          <Box
                            component="span"
                            sx={{
                              position: 'absolute',
                              right: 3,
                              bottom: 1,
                              fontSize: '0.55rem',
                              lineHeight: 1,
                              color: pct > 70 ? 'error.main' : 'primary.main',
                              fontWeight: 600,
                              pointerEvents: 'none',
                            }}
                          >
                            {pct.toFixed(1)}%
                          </Box>
                        );
                      })()}
                    </TableCell>

                    {showGroup1 && (
                      <>
                        {/* รอจ่าย (col35) — เหมือนคอลัมน์ Expected ใน BudgetTable.js */}
                        <TableCell
                          sx={{ ...TD_SX, textAlign: 'right', width: getW(35) }}
                          onContextMenu={zeroExcludeHandler('expected_value', getExpectedValue(row))}
                        >
                          <NumCell value={getExpectedValue(row)} redWhenNegative redWhenPositive />
                        </TableCell>

                        {/* AP รอจ่าย — รวม pending_items ที่ยังไม่จ่ายจากทุก budget ของโปรเจกต์นี้ */}
                        <TableCell
                          sx={{
                            ...TD_SX,
                            textAlign: 'right',
                            width: getW(32),
                          }}
                          onContextMenu={zeroExcludeHandler('ap_pending_unpaid', row.ap_pending_unpaid)}
                        >
                          <NumCell value={row.ap_pending_unpaid} redWhenPositive />
                        </TableCell>

                        {/* col16: ต้องใช้อีก */}
                        <TableCell
                          sx={{ ...TD_SX, textAlign: 'right', width: getW(16) }}
                          onContextMenu={zeroExcludeHandler('to_spend', col16)}
                        >
                          <InlineAmountCell value={col16} alwaysRed />
                        </TableCell>

                        {/* col12: BGเหลือที่มีสิทธิจ่าย (นิยามเดียวกับ P111) */}
                        <TableCell
                          sx={{ ...TD_SX, textAlign: 'right', width: getW(12) }}
                          onContextMenu={zeroExcludeHandler('col12_payable_bg_eligible', col12)}
                        >
                          <NumCell value={col12} redWhenNegative redWhenPositive />
                        </TableCell>

                        {/* col34: รอเงินคืน */}
                        <TableCell
                          sx={{ ...TD_SX, textAlign: 'right', width: getW(34) }}
                          onContextMenu={zeroExcludeHandler('refund_pending', getRefundPendingValue(row))}
                        >
                          <NumCell value={getRefundPendingValue(row)} color="success.main" />
                        </TableCell>
                      </>
                    )}

                    {/* col17: สรุปอนาคตต้องจ่ายอีกรวม = รอจ่าย(col35) + AP รอจ่าย(col32) + ต้องใช้อีก(col16) + BGเหลือที่มีสิทธิจ่าย(col12) (รอเงินคืน col34 หักอยู่ใน BG PAID แล้ว) */}
                    <TableCell
                      sx={{ ...TD_SX, textAlign: 'right', width: getW(17) }}
                      onContextMenu={zeroExcludeHandler('col17', col17)}
                    >
                      <NumCell value={col17} redWhenPositive />
                    </TableCell>

                    {/* payment dates group: col 27-31-28-29-30 */}
                    {showGroup1 &&
                      (() => {
                        const deliveryDate = getVal(row, 'delivery_date');
                        const creditDays = row.client_id?.credit_days;
                        const paymentCycleDay = getVal(
                          row,
                          'payment_cycle_day',
                        );
                        let expectedDate = null;
                        if (
                          deliveryDate &&
                          creditDays != null &&
                          creditDays !== ''
                        ) {
                          const base = new Date(deliveryDate);
                          base.setDate(base.getDate() + Number(creditDays));
                          if (
                            paymentCycleDay != null &&
                            paymentCycleDay !== ''
                          ) {
                            const day = Number(paymentCycleDay);
                            // หาวันรอบจ่ายถัดไปที่ >= base
                            const sameMonth = new Date(
                              base.getFullYear(),
                              base.getMonth(),
                              day,
                            );
                            expectedDate =
                              sameMonth >= base
                                ? sameMonth
                                : new Date(
                                    base.getFullYear(),
                                    base.getMonth() + 1,
                                    day,
                                  );
                          } else {
                            expectedDate = base;
                          }
                        }
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const daysLeft = expectedDate
                          ? Math.round(
                              (expectedDate.getTime() - today.getTime()) /
                                (1000 * 60 * 60 * 24),
                            )
                          : null;
                        const daysColor =
                          daysLeft === null
                            ? 'text.disabled'
                            : daysLeft < 0
                            ? '#dc2626'
                            : daysLeft <= 7
                            ? '#d97706'
                            : '#059669';
                        return (
                          <>
                            {/* col 27 */}
                            <TableCell
                              sx={{
                                ...TD_SX,
                                position: 'relative',
                                textAlign: 'center',
                                userSelect: 'none',
                              }}
                              onContextMenu={zeroExcludeHandler(
                                'delivery_date',
                                deliveryDate ? new Date(deliveryDate).getTime() : 0,
                              )}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.65rem',
                                  color: deliveryDate
                                    ? 'text.primary'
                                    : 'text.disabled',
                                }}
                              >
                                {fmtDate(deliveryDate) || '-'}
                              </Typography>
                            </TableCell>
                            {/* col 28 */}
                            <TableCell
                              sx={{ ...TD_SX, textAlign: 'center' }}
                              onContextMenu={zeroExcludeHandler('credit_days', creditDays ?? 0)}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.65rem',
                                  color:
                                    creditDays != null && creditDays !== ''
                                      ? 'text.primary'
                                      : 'text.disabled',
                                }}
                              >
                                {creditDays != null && creditDays !== ''
                                  ? `${creditDays}`
                                  : '-'}
                              </Typography>
                            </TableCell>
                            {/* col 31 — รอบการจ่ายเงิน (วันที่ในเดือน) */}
                            <TableCell
                              sx={{
                                ...TD_SX,
                                position: 'relative',
                                textAlign: 'center',
                                userSelect: 'none',
                              }}
                              onContextMenu={zeroExcludeHandler(
                                'payment_cycle_day',
                                paymentCycleDay != null && paymentCycleDay !== ''
                                  ? Number(paymentCycleDay)
                                  : 0,
                              )}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.65rem',
                                  color:
                                    paymentCycleDay != null &&
                                    paymentCycleDay !== ''
                                      ? 'text.primary'
                                      : 'text.disabled',
                                  fontWeight:
                                    paymentCycleDay != null &&
                                    paymentCycleDay !== ''
                                      ? 600
                                      : 400,
                                }}
                              >
                                {paymentCycleDay != null &&
                                paymentCycleDay !== ''
                                  ? `ทุกที่ ${paymentCycleDay}`
                                  : '-'}
                              </Typography>
                            </TableCell>
                            {/* col 29 */}
                            <TableCell
                              sx={{ ...TD_SX, textAlign: 'center' }}
                              onContextMenu={zeroExcludeHandler(
                                'expected_date',
                                expectedDate ? expectedDate.getTime() : 0,
                              )}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.65rem',
                                  color: expectedDate
                                    ? 'text.primary'
                                    : 'text.disabled',
                                }}
                              >
                                {expectedDate ? fmtDate(expectedDate) : '-'}
                              </Typography>
                            </TableCell>
                            {/* col 30 */}
                            <TableCell
                              sx={{ ...TD_SX, textAlign: 'center' }}
                              onContextMenu={zeroExcludeHandler('days_left', daysLeft ?? 0)}
                            >
                              {daysLeft !== null ? (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: daysLeft < 0 ? 700 : 600,
                                    fontSize: '0.65rem',
                                    color: daysColor,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {daysLeft < 0
                                    ? `เกิน ${-daysLeft}`
                                    : daysLeft === 0
                                    ? 'วันนี้'
                                    : `+${daysLeft}`}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.65rem',
                                    color: 'text.disabled',
                                  }}
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          </>
                        );
                      })()}

                    {/* col15: BG คงเหลือ = max(col10 - col17, 0) */}
                    <TableCell
                      sx={{ ...TD_SX, textAlign: 'right', width: getW(15) }}
                      onContextMenu={zeroExcludeHandler('net_remaining', col15)}
                    >
                      <NumCell value={col15} color="#16a34a" />
                    </TableCell>

                    {/* col14: เกิน BG = col10-col17 เฉพาะค่า < 0 */}
                    <TableCell
                      sx={{ ...TD_SX, textAlign: 'right', width: getW(14) }}
                      onContextMenu={zeroExcludeHandler('over_budget', col14 ?? 0)}
                    >
                      {col14 !== null ? (
                        <NumCell value={col14} redWhenNegative />
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.65rem',
                            color: '#cbd5e1',
                            display: 'block',
                            textAlign: 'right',
                          }}
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* กำไร */}
                    <TableCell
                      sx={{
                        ...TD_SX,
                        textAlign: 'right',
                        width: getW(19),
                        bgcolor: realprofit >= 0 ? 'rgba(34, 197, 94, 0.25)' : 'rgba(239, 68, 68, 0.25)',
                      }}
                      onContextMenu={zeroExcludeHandler('realprofit', realprofit)}
                    >
                      <NumCell
                        value={realprofit}
                        bold
                        color={realprofit >= 0 ? '#166534' : '#991b1b'}
                      />
                    </TableCell>

                    {/* % BG ใช้ */}
                    <TableCell
                      sx={{ ...TD_SX, textAlign: 'right' }}
                      onContextMenu={zeroExcludeHandler('bg_used_pct', bgUsedPct)}
                    >
                      <PctCell
                        value={bgUsedPct}
                        redWhenOver={100}
                        orangeWhenOver={50}
                      />
                    </TableCell>

                    {/* % กำไร */}
                    <TableCell
                      sx={{ ...TD_SX, textAlign: 'right' }}
                      onContextMenu={zeroExcludeHandler('profit_pct', realPct)}
                    >
                      <PctCell value={realPct} redWhenNegative />
                    </TableCell>

                    {/* หมายเหตุ */}
                    <TableCell sx={TD_SX}>
                      <InlineNoteCell value={latestNote} />
                    </TableCell>

      </TableRow>
  );
});

export default ProjectDashboardRow;
